<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { getContext, onDestroy, untrack } from 'svelte';
    import { __, generateElementId } from '@oat-sa-private/ui-core';
    import { Button } from '@oat-sa-private/ui-elements';
    import Prompt from '../../../Prompt.svelte';
    import { getInteractionStateStore } from '../../../../itemsStateStore.js';
    import { getItemPendingOperationsStore } from '../../../../itemsPendingOperationsStore.js';
    import {
        attachmentsServiceFactory,
        defaultGetAttachmentsUploadData,
        uploadServiceStatuses
    } from '../../../../services/upload/attachmentsService.js';
    import UploadProgress from '../../../../services/upload/UploadProgress.svelte';
    import AtomicAriaLive from '../../../AtomicAriaLive.svelte';
    import {
        createRecordingEvidence,
        createVideoRecordingFile,
        formatBytes,
        formatDuration,
        downloadRemoteFile,
        getVideoDownloadTarget,
        selectSupportedVideoMimeType
    } from './helpers.js';
    import { restoreVideoInteractionState, persistVideoInteractionState } from './state.js';
    import { persistRecordedVideoFile } from './upload.js';

    const qtiClass = 'qti-customInteraction';
    const cardinality = 'single';
    const recordStates = Object.freeze({
        idle: 'idle',
        preparing: 'preparing',
        recording: 'recording',
        stopping: 'stopping'
    });

    let {
        itemIdentifier,
        responseIdentifier,
        properties = {},
        typeIdentifier = 'videoRecordingInteraction',
        prompt,
        role,
        ariaAttrs = {},
        language,
        id,
        classes = '',
        dir,
        dataAttrs = {},
        disabled = false,
        timeout = 60 * 1000
    } = $props();

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const logger = itemContext?.getLogger();
    const instructionsLang = itemContext?.getInstructionsLang();

    // svelte-ignore state_referenced_locally
    const interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier),
        pendingOperationsStore = getItemPendingOperationsStore(itemIdentifier);

    const recordKey = generateElementId('recordKey');
    const uploadKey = generateElementId('uploadKey');
    const ariaLiveContainerId = generateElementId('live');
    const ariaLiveStrings = Object.freeze({
        started: __('Preparing saved response.'),
        completed: __('Saved response ready.'),
        cancelled: __('Saved response unavailable.'),
        reset: __('Response file cleared.')
    });

    // svelte-ignore non_reactive_update
    let isReviewMode,
        allowPlayback,
        hideStopButton,
        autoStart,
        hideRecordButton,
        autoPlayback,
        playSound,
        countdownBeforeStartRaw,
        maxAttemptsRaw,
        maxRecordingTimeRaw;

    setDerivedProps();

    function setDerivedProps() {
        isReviewMode = !!properties?.isReviewMode;
        allowPlayback =
            isReviewMode ||
            (properties?.allowPlayback !== false &&
                properties?.allowPlayback !== 'false' &&
                properties?.allowPlayback !== '0' &&
                properties?.allowPlayback !== '');
        hideStopButton =
            properties?.hideStopButton === true ||
            properties?.hideStopButton === 'true' ||
            properties?.hideStopButton === '1';
        autoStart = properties?.autoStart === true || properties?.autoStart === 'true' || properties?.autoStart === '1';
        hideRecordButton =
            properties?.hideRecordButton === true ||
            properties?.hideRecordButton === 'true' ||
            properties?.hideRecordButton === '1';
        autoPlayback =
            properties?.autoPlayback === true ||
            properties?.autoPlayback === 'true' ||
            properties?.autoPlayback === '1';
        playSound = properties?.playSound === true || properties?.playSound === 'true' || properties?.playSound === '1';
        countdownBeforeStartRaw = Number(properties?.countdownBeforeStart);
        maxAttemptsRaw = Number(properties?.maxAttempts);
        maxRecordingTimeRaw = Number(properties?.maxRecordingTime);
    }
    const countdownBeforeStartSeconds = Math.max(
        0,
        Math.floor(Number.isFinite(countdownBeforeStartRaw) ? countdownBeforeStartRaw : 0)
    );

    const maxAttempts = Math.max(0, Math.floor(Number.isFinite(maxAttemptsRaw) ? maxAttemptsRaw : 2));
    const maxRecordingTimeSeconds = Math.max(
        0,
        Math.floor(Number.isFinite(maxRecordingTimeRaw) ? maxRecordingTimeRaw : 120)
    );
    const preferredMimeType = selectSupportedVideoMimeType();

    let effectivePrompt = $derived(prompt || normalizePrompt(properties?.prompt));

    let recordState = $state(recordStates.idle);
    let persistenceState = $state(uploadServiceStatuses.initial);
    let liveVideoElement = $state();
    let previewVideoElement = $state();
    let liveStream = null;
    let mediaRecorder = null;
    let recordingStartedAt = 0;
    let stopTimeoutId = null;
    let autoStartTimeoutId = null;
    let autoStartIntervalId = null;
    const uploadStats = $state({ bytesLoaded: 0, bytesTotal: 0 });
    let uploadError = $state('');
    let ariaLiveAnnouncement = $state({});
    let ignoreNextStop = false;
    let destroyed = false;
    let isInteractionFocused = $state(false);
    let autoStartPending = $state(false);
    let autoStartCountdownSeconds = $state(0);
    let autoStartTriggered = $state(false);
    let completedRecordingsCount = $state(0);
    let lastAutoplayPreviewUrl = $state('');
    let cueAudioContext = null;
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const localObjectUrls = new Set();

    // svelte-ignore state_referenced_locally
    const attachmentsService = attachmentsServiceFactory({
        getAttachmentsUploadData: itemContext?.getGetAttachmentsUploadData?.() || defaultGetAttachmentsUploadData,
        itemIdentifier,
        responseIdentifier,
        timeout
    });

    const supportsRecording =
        !isReviewMode &&
        typeof navigator !== 'undefined' &&
        !!navigator.mediaDevices?.getUserMedia &&
        typeof MediaRecorder !== 'undefined';

    function normalizePrompt(promptValue) {
        if (Array.isArray(promptValue)) {
            return promptValue;
        }

        if (typeof promptValue === 'string' && promptValue.trim()) {
            return [{ type: 'html', content: promptValue }];
        }

        return null;
    }

    const restoredState = restoreVideoInteractionState({
        interactionStateStore,
        localObjectUrls
    });
    let previewUrl = $state(restoredState.previewUrl);
    let responseNotice = $state(restoredState.responseNotice);
    let evidence = $state(restoredState.evidence);
    let storedResponseValue = $state(restoredState.storedResponseValue);
    // svelte-ignore state_referenced_locally
    completedRecordingsCount = storedResponseValue ? 1 : 0;

    if (restoredState.shouldInitializeEmptyState) {
        persistState(null);
    }

    let isRecording = $derived(recordState === recordStates.recording);
    let isPreparing = $derived(recordState === recordStates.preparing);
    let isStopping = $derived(recordState === recordStates.stopping);
    let isLoading = $derived(persistenceState === uploadServiceStatuses.loading);
    let isUploading = $derived(persistenceState === uploadServiceStatuses.uploading);
    let isPersisting = $derived(isLoading || isUploading);
    let hasStoredResponse = $derived(!!storedResponseValue);
    let controlsDisabled = $derived(disabled || isPreparing || isStopping || isPersisting);

    let reviewDownloadTarget = $derived(isReviewMode ? getVideoDownloadTarget(storedResponseValue) : null);
    let reviewDownloadError = $state('');

    async function handleReviewDownload() {
        if (!reviewDownloadTarget) {
            return;
        }

        try {
            await downloadRemoteFile(reviewDownloadTarget.href, reviewDownloadTarget.filename);
            reviewDownloadError = '';
        } catch (error) {
            reviewDownloadError = __('Unable to download this recording. Please try again.');
            logger?.error(error);
        }
    }

    const hasAttemptLimit = maxAttempts > 0;
    let hasReachedAttemptLimit = $derived(hasAttemptLimit && completedRecordingsCount >= maxAttempts);
    let canStartRecording = $derived(
        supportsRecording && !controlsDisabled && !isRecording && !isPreparing && !isStopping && !hasReachedAttemptLimit
    );
    const showStartButton = !isReviewMode && !hideRecordButton;
    const showStopButton = !isReviewMode && (!hideStopButton || maxRecordingTimeSeconds <= 0);
    let startButtonDisabled = $derived(!canStartRecording || autoStartPending);
    let stopButtonDisabled = $derived(!isRecording);
    let resetButtonDisabled = $derived(controlsDisabled && !previewUrl);
    let autoStartEnabled = $derived(
        autoStart &&
            !isReviewMode &&
            !previewUrl &&
            !hasStoredResponse &&
            supportsRecording &&
            !disabled &&
            !isPersisting &&
            !isRecording &&
            !isPreparing &&
            !isStopping &&
            !hasReachedAttemptLimit
    );
    let autoStartCountdownMessage = $derived(
        autoStartPending && autoStartCountdownSeconds > 0
            ? __('Recording starts in %d seconds.', autoStartCountdownSeconds)
            : ''
    );

    let helperInstruction = $derived(
        getHelperInstruction({
            autoStartCountdownMessage,
            isInteractionFocused,
            isReviewMode,
            hasReachedAttemptLimit,
            isRecording,
            hideStopButton,
            previewUrl,
            allowPlayback
        })
    );
    let summaryRows = $derived([
        { label: __('Container / MIME'), value: evidence?.mimeType || preferredMimeType || __('Unavailable') },
        { label: __('Duration'), value: evidence ? formatDuration(evidence.durationMs) : __('Not recorded') },
        { label: __('File size'), value: evidence ? formatBytes(evidence.sizeBytes) : __('Not recorded') },
        {
            label: __('Average bitrate'),
            value: evidence?.averageBitrateKbps ? `${evidence.averageBitrateKbps} kbps` : __('Not available')
        },
        {
            label: __('Save mode'),
            value: evidence?.persistenceStrategy || __('Not saved yet')
        }
    ]);
    $effect(() => {
        if (autoStartEnabled && !autoStartPending && !autoStartTriggered) {
            untrack(() => {
                scheduleAutoStart();
            });
        }
    });
    $effect(() => {
        if (!autoStartEnabled && autoStartPending) {
            untrack(() => {
                clearAutoStartCountdown();
            });
        }
    });
    $effect(() => {
        if (previewUrl !== lastAutoplayPreviewUrl && !previewUrl) {
            untrack(() => {
                lastAutoplayPreviewUrl = '';
            });
        }
    });
    $effect(() => {
        if (
            autoPlayback &&
            allowPlayback &&
            previewUrl &&
            previewVideoElement &&
            previewUrl !== lastAutoplayPreviewUrl &&
            !isRecording &&
            !isPreparing &&
            !isStopping
        ) {
            untrack(() => {
                lastAutoplayPreviewUrl = previewUrl;
                const playPromise = previewVideoElement.play();
                playPromise?.catch(() => undefined);
            });
        }
    });

    function persistState(responseValue, validity = true, options = {}) {
        storedResponseValue = responseValue;
        persistVideoInteractionState({
            interactionStateStore,
            qtiClass,
            typeIdentifier,
            cardinality,
            responseValue,
            evidence: $state.snapshot(evidence),
            responseNotice: $state.snapshot(responseNotice),
            previewUrl: $state.snapshot(previewUrl),
            validity,
            ...options
        });
    }

    function getHelperInstruction({
        autoStartCountdownMessage: countdownMessage,
        isInteractionFocused: interactionFocused,
        isReviewMode: reviewMode,
        hasReachedAttemptLimit: reachedAttemptLimit,
        isRecording: recording,
        hideStopButton: stopButtonHidden,
        previewUrl: currentPreviewUrl,
        allowPlayback: playbackAllowed
    }) {
        if (countdownMessage) {
            return countdownMessage;
        }

        if (!interactionFocused || reviewMode) {
            return '';
        }

        if (reachedAttemptLimit) {
            return __('Maximum recording attempts reached.');
        }

        if (recording && stopButtonHidden) {
            return __('Recording is in progress and will stop automatically when the time limit is reached.');
        }

        if (currentPreviewUrl && !playbackAllowed) {
            return __('Playback is disabled for this recording by item configuration.');
        }

        if (recording) {
            return __('Recording is in progress. Use Stop to finish or wait for the time limit.');
        }

        if (currentPreviewUrl) {
            return __('Use the video controls to play back the current recording. Use Reset to clear it.');
        }

        return __('Use Start recording to begin. Use Stop to finish. Use Reset to clear the current response.');
    }

    function clearAutoStartCountdown() {
        if (autoStartIntervalId) {
            clearInterval(autoStartIntervalId);
            autoStartIntervalId = null;
        }
        if (autoStartTimeoutId) {
            clearTimeout(autoStartTimeoutId);
            autoStartTimeoutId = null;
        }
        autoStartPending = false;
        autoStartCountdownSeconds = 0;
    }

    function scheduleAutoStart() {
        if (!autoStartEnabled || autoStartPending) {
            return;
        }

        autoStartTriggered = true;
        autoStartPending = true;
        autoStartCountdownSeconds = countdownBeforeStartSeconds;
        const startDelayMs = Math.max(0, countdownBeforeStartSeconds) * 1000;

        if (countdownBeforeStartSeconds > 0) {
            autoStartIntervalId = setInterval(() => {
                if (autoStartCountdownSeconds > 0) {
                    autoStartCountdownSeconds -= 1;
                }
            }, 1000);
        }

        autoStartTimeoutId = setTimeout(() => {
            clearAutoStartCountdown();
            startRecording();
        }, startDelayMs);
    }

    function clearStopTimeout() {
        if (stopTimeoutId) {
            clearTimeout(stopTimeoutId);
            stopTimeoutId = null;
        }
    }

    function releaseStream() {
        if (liveStream) {
            liveStream.getTracks().forEach(track => track.stop());
        }

        if (liveVideoElement) {
            liveVideoElement.srcObject = null;
        }

        liveStream = null;
    }

    function createLocalPreviewUrl(file) {
        const objectUrl = URL.createObjectURL(file);
        localObjectUrls.add(objectUrl);
        return objectUrl;
    }

    function getCueAudioContext() {
        if (cueAudioContext) {
            return cueAudioContext;
        }

        const AudioContextCtor = globalThis.AudioContext || globalThis.webkitAudioContext;
        if (!AudioContextCtor) {
            return null;
        }

        cueAudioContext = new AudioContextCtor();
        return cueAudioContext;
    }

    function playCueSound(frequency) {
        if (!playSound || isReviewMode) {
            return;
        }

        const context = getCueAudioContext();
        if (!context) {
            return;
        }

        try {
            if (context.state === 'suspended') {
                context.resume?.();
            }

            const oscillator = context.createOscillator();
            const gain = context.createGain();
            const endTime = (context.currentTime || 0) + 0.08;

            oscillator.type = 'sine';
            oscillator.frequency.value = frequency;
            gain.gain.value = 0.08;
            oscillator.connect(gain);
            gain.connect(context.destination);
            oscillator.start();
            oscillator.stop(endTime);
        } catch (error) {
            logger?.error(error);
        }
    }

    function revokeLocalPreviewUrls() {
        localObjectUrls.forEach(url => URL.revokeObjectURL(url));
        localObjectUrls.clear();
    }

    async function persistRecordedFile(file, recorderMimeType, recordingDurationMs, localPreviewUrl) {
        await persistRecordedVideoFile({
            file,
            recorderMimeType,
            recordingDurationMs,
            localPreviewUrl,
            itemIdentifier,
            responseIdentifier,
            attachmentsService,
            pendingOperationsStore,
            recordKey,
            uploadKey,
            uploadStats,
            logger,
            setPersistenceState(value) {
                persistenceState = value;
            },
            setUploadError(value) {
                uploadError = value;
            },
            setResponseNotice(value) {
                responseNotice = value;
            },
            setEvidence(value) {
                evidence = value;
            },
            setAnnouncement(value) {
                ariaLiveAnnouncement.text = value;
            },
            persistState,
            ariaLiveStrings
        });
    }

    async function startRecording() {
        clearAutoStartCountdown();
        if (!canStartRecording) {
            return;
        }

        if (!preferredMimeType) {
            uploadError = __('No supported recording MIME type was found for this browser.');
            return;
        }

        uploadError = '';
        responseNotice = '';
        clearStopTimeout();
        recordState = recordStates.preparing;

        try {
            liveStream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true
            });

            if (liveVideoElement) {
                liveVideoElement.srcObject = liveStream;
            }

            const chunks = [];

            mediaRecorder = new MediaRecorder(liveStream, {
                mimeType: preferredMimeType
            });

            mediaRecorder.addEventListener('dataavailable', event => {
                if (event.data?.size) {
                    chunks.push(event.data);
                }
            });

            mediaRecorder.addEventListener(
                'stop',
                async () => {
                    clearStopTimeout();
                    releaseStream();

                    if (ignoreNextStop || destroyed) {
                        ignoreNextStop = false;
                        pendingOperationsStore.delete(recordKey);
                        recordState = recordStates.idle;
                        return;
                    }

                    playCueSound(660);
                    const recorderMimeType = mediaRecorder.mimeType || preferredMimeType;
                    const blob = new Blob(chunks, {
                        type: recorderMimeType
                    });
                    const { file, recorderMimeType: recordedMimeType } = createVideoRecordingFile({
                        blob,
                        responseIdentifier,
                        mimeType: recorderMimeType
                    });
                    const recordingDurationMs = Date.now() - recordingStartedAt;

                    revokeLocalPreviewUrls();
                    const localPreviewUrl = createLocalPreviewUrl(file);

                    await persistRecordedFile(file, recordedMimeType, recordingDurationMs, localPreviewUrl);
                    completedRecordingsCount += 1;
                    previewUrl = localPreviewUrl;
                    if (!evidence) {
                        evidence = createRecordingEvidence({
                            sizeBytes: file.size,
                            durationMs: recordingDurationMs,
                            mimeType: recordedMimeType,
                            persistenceStrategy: 'recorded-local-preview-ready'
                        });
                    }
                    recordState = recordStates.idle;
                },
                { once: true }
            );

            recordingStartedAt = Date.now();
            mediaRecorder.start(1000);
            playCueSound(880);
            recordState = recordStates.recording;
            pendingOperationsStore.add(recordKey);

            if (maxRecordingTimeSeconds > 0) {
                stopTimeoutId = setTimeout(() => stopRecording(), maxRecordingTimeSeconds * 1000);
            }
        } catch (error) {
            uploadError = __('Unable to start video recording. Check browser permissions and try again.');
            logger?.error(error);
            releaseStream();
            recordState = recordStates.idle;
            pendingOperationsStore.delete(recordKey);
        }
    }

    function stopRecording() {
        clearAutoStartCountdown();
        if (!mediaRecorder || mediaRecorder.state !== 'recording') {
            return;
        }

        recordState = recordStates.stopping;
        mediaRecorder.stop();
    }

    function handleInteractionFocusIn() {
        if (!isInteractionFocused) {
            isInteractionFocused = true;
        }
    }

    function preventRestrictedPlayback(event) {
        if (allowPlayback) {
            return;
        }

        const videoElement = event.currentTarget;
        if (!videoElement) {
            return;
        }

        videoElement.pause?.();
        if (videoElement.currentTime !== 0) {
            videoElement.currentTime = 0;
        }
    }

    function handleVideoContextMenu(event) {
        if (!isReviewMode) {
            event.preventDefault();
        }
    }

    function resetResponse() {
        clearAutoStartCountdown();
        autoStartTriggered = false;
        clearStopTimeout();

        if (mediaRecorder?.state === 'recording') {
            ignoreNextStop = true;
            mediaRecorder.stop();
        }

        releaseStream();
        revokeLocalPreviewUrls();
        uploadError = '';
        responseNotice = '';
        evidence = null;
        storedResponseValue = null;
        recordState = recordStates.idle;
        persistenceState = uploadServiceStatuses.initial;
        previewUrl = '';
        persistState(null);
        pendingOperationsStore.delete(recordKey);
        pendingOperationsStore.delete(uploadKey);
        ariaLiveAnnouncement.text = ariaLiveStrings.reset;
    }

    onDestroy(() => {
        destroyed = true;
        clearAutoStartCountdown();
        clearStopTimeout();

        if (mediaRecorder?.state === 'recording') {
            ignoreNextStop = true;
            mediaRecorder.stop();
        }

        attachmentsService.abortController.abort('destroyed');
        attachmentsService.uploadService?.cancel('destroyed');
        releaseStream();
        revokeLocalPreviewUrls();
        const closePromise = cueAudioContext?.close?.();
        closePromise?.catch?.(() => undefined);
        cueAudioContext = null;
        pendingOperationsStore.delete(recordKey);
        pendingOperationsStore.delete(uploadKey);
        previewUrl = '';
    });
</script>

<style>
    .video-recording {
        display: grid;
        gap: var(--space-2x);
    }

    .video-surface {
        background: var(--color-bg-default);
        border: 1px solid var(--color-gs-light-hover-bg);
        border-radius: var(--radius-large);
        overflow: hidden;
    }

    .video-stage {
        background: var(--color-gs-dark);
        display: flex;
        justify-content: center;
        min-height: 240px;
        width: 100%;
    }

    .video-stage :global(video) {
        display: block;
        max-height: 420px;
        min-height: 240px;
        width: 100%;
    }

    .video-toolbar {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-half);
        padding: var(--space-threequarters);
    }

    .uploading {
        padding: 0 var(--space-threequarters) var(--space-threequarters);
    }

    .status-pill {
        background: var(--color-bg-info);
        border-radius: var(--radius-pill);
        color: var(--color-text-default);
        font-size: var(--fontsize-body-s);
        padding: var(--space-quarter) var(--space-threequarters);
    }

    .status-pill.recording {
        background: var(--color-alert-alternative-bg);
        color: var(--color-alert-hover);
    }

    .status-pill.persisting {
        background: var(--color-brand-light);
        color: var(--color-brand);
    }

    .download-link {
        padding: 0;
        border: 0;
        background: transparent;
        font-size: var(--fontsize-body-s);
        font-weight: 600;
        color: var(--color-brand);
        text-decoration: underline;
        cursor: pointer;
    }

    .evidence {
        border: 1px solid var(--color-gs-light-hover-bg);
        border-radius: var(--radius-large);
        padding: var(--space-threequarters);
    }

    .evidence dl {
        display: grid;
        gap: var(--space-half);
        grid-template-columns: minmax(10rem, 14rem) minmax(0, 1fr);
        margin: 0;
    }

    .evidence dt {
        color: var(--color-text-feedback);
        font-weight: 600;
    }

    .evidence dd {
        margin: 0;
        word-break: break-word;
    }

    .message {
        color: var(--color-text-feedback);
        margin: 0;
    }

    .message.info {
        color: var(--color-text-default);
    }

    .message.error {
        color: var(--color-alert);
    }
</style>

<div
    class="qti-interaction qti-blockInteraction {qtiClass} {classes}"
    data-type-identifier={typeIdentifier}
    onfocusin={handleInteractionFocusIn}
    lang={language}
    {id}
    {dir}
    {role}
    {...ariaAttrs}
    {...dataAttrs}>
    {#if effectivePrompt}
        <Prompt blockTree={effectivePrompt} />
    {/if}

    <div class="video-recording">
        <div class="video-surface">
            <div class="video-stage">
                {#if isRecording || isPreparing}
                    <video
                        bind:this={liveVideoElement}
                        autoplay
                        muted
                        playsinline
                        oncontextmenu={handleVideoContextMenu}></video>
                {:else if previewUrl}
                    {#if allowPlayback}
                        <video
                            bind:this={previewVideoElement}
                            src={previewUrl}
                            controls
                            playsinline
                            preload="auto"
                            oncontextmenu={handleVideoContextMenu}><track kind="captions" src="" /></video>
                    {:else}
                        <video
                            bind:this={previewVideoElement}
                            src={previewUrl}
                            playsinline
                            muted
                            tabindex="-1"
                            aria-hidden="true"
                            onplay={preventRestrictedPlayback}
                            oncontextmenu={handleVideoContextMenu}></video>
                        <div class="video-toolbar">
                            <p class="message">
                                {__('Playback is disabled for this recording by item configuration.')}
                            </p>
                        </div>
                    {/if}
                {:else}
                    <div class="video-toolbar">
                        <p class="message">
                            {#if isReviewMode}
                                {__('No uploaded video is available for review.')}
                            {:else}
                                {__(
                                    'Record a response to play it back locally. Sandbox/manual mode can also keep a local saved response for this session.'
                                )}
                            {/if}
                        </p>
                    </div>
                {/if}
            </div>

            {#if isUploading}
                <div class="uploading">
                    <UploadProgress {...uploadStats} cancelable={false} />
                </div>
            {/if}

            <div class="video-toolbar">
                <span class="status-pill" class:recording={isRecording} class:persisting={isPersisting}>
                    {#if isRecording}
                        {__('Recording')}
                    {:else if isPreparing}
                        {__('Preparing camera')}
                    {:else if isStopping}
                        {__('Finalizing local recording')}
                    {:else if isLoading}
                        {__('Preparing saved response')}
                    {:else if isUploading}
                        {__('Uploading saved response')}
                    {:else if hasStoredResponse}
                        {__('Saved response ready')}
                    {:else if previewUrl}
                        {__('Local playback ready')}
                    {:else}
                        {__('Ready')}
                    {/if}
                </span>

                {#if reviewDownloadTarget}
                    <button type="button" class="download-link" onclick={handleReviewDownload}>
                        {__('Download')}
                    </button>
                {/if}

                {#if showStartButton}
                    <Button label={__('Start recording')} disabled={startButtonDisabled} on:click={startRecording} />
                {/if}
                {#if showStopButton}
                    <Button label={__('Stop')} disabled={stopButtonDisabled} on:click={stopRecording} />
                {/if}
                {#if !isReviewMode}
                    <Button label={__('Reset')} disabled={resetButtonDisabled} on:click={resetResponse} />
                {/if}
            </div>
        </div>

        {#if uploadError}
            <p class="message error">{uploadError}</p>
        {/if}
        {#if reviewDownloadError}
            <p class="message error">{reviewDownloadError}</p>
        {/if}

        {#if helperInstruction && !uploadError && !responseNotice}
            <p class="message info">{helperInstruction}</p>
        {/if}

        {#if responseNotice}
            <p class="message info">{responseNotice}</p>
        {/if}

        <div class="evidence">
            <dl>
                {#each summaryRows as row (row.label)}
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                {/each}
            </dl>
        </div>
    </div>

    <AtomicAriaLive id={ariaLiveContainerId} announcement={ariaLiveAnnouncement} lang={instructionsLang} />
</div>
